<script setup lang="ts">
    import ChatAvatar from './ChatAvatar.vue';
    import ChatBlockRow from './ChatBlockRow.vue';
    import ChatVehicleSearchTabs from './ChatVehicleSearchTabs.vue';
    import type {ReturnLine} from './chatApi';
    import ChatMessage from './ChatMessage.vue';
    import ChatReturnRequest from './ChatReturnRequest.vue';
    import ChatOrderStatusCard from './ChatOrderStatusCard.vue';
    import ChatCredentialsForm from './ChatCredentialsForm.vue';
    import ChatChoiceOptions from './ChatChoiceOptions.vue';
    import ChatProductResults from './ChatProductResults.vue';
    import ChatVehicleCandidates from './ChatVehicleCandidates.vue';
    import ChatComparisonTable from './ChatComparisonTable.vue';

    defineProps<{entries: Record<string, any>[]}>();

    const emit = defineEmits<{
        (event: 'compare', names: string[]): void;
        (event: 'choose', choice: {label: string; choiceId: string | null}): void;
        (event: 'search-plate', plate: string): void;
        (event: 'search-vin', vin: string): void;
        (event: 'select-vehicle-candidate', candidate: Record<string, any>): void;
        (event: 'verify', credentials: {orderId: string; emailAddress: string}): void;
        (event: 'verify-order', credentials: {orderId: string; emailAddress: string}): void;
        (event: 'submit-return', orderLines: ReturnLine[]): void;
        (event: 'cancel-return'): void;
    }>();
</script>

<template>
    <div class="messages">
        <!-- The welcome state; the parent decides whether there is a conversation yet. -->
        <slot />

        <template
            v-for="entry in entries"
            :key="entry.id">
            <ChatMessage
                v-if="entry.kind === 'message'"
                :data-entry-id="entry.id"
                :label="entry.label"
                :text="entry.text"
                :tone="entry.tone"
            />

            <ChatBlockRow
                v-else-if="entry.kind === 'plateSearch'"
                :data-entry-id="entry.id">
                <ChatVehicleSearchTabs
                    :disabled="entry.stale"
                    @search-plate="(plate) => emit('search-plate', plate)"
                    @search-vin="(vin) => emit('search-vin', vin)" />
            </ChatBlockRow>

            <ChatBlockRow
                v-else-if="entry.kind === 'products'"
                :data-entry-id="entry.id">
                <ChatProductResults
                    :products="entry.products"
                    :meta="entry.meta"
                    :disabled="entry.stale"
                    @compare="(names: string[]) => emit('compare', names)" />
            </ChatBlockRow>

            <ChatBlockRow
                v-else-if="entry.kind === 'comparison'"
                :data-entry-id="entry.id">
                <ChatComparisonTable :table="entry.table" />
            </ChatBlockRow>

            <ChatBlockRow
                v-else-if="entry.kind === 'vehicleCandidates'"
                :data-entry-id="entry.id">
                <ChatVehicleCandidates
                    :candidates="entry.candidates"
                    :disabled="entry.stale"
                    @select="(candidate: Record<string, any>) => emit('select-vehicle-candidate', candidate)" />
            </ChatBlockRow>

            <ChatBlockRow
                v-else-if="entry.kind === 'choices'"
                :data-entry-id="entry.id">
                <ChatChoiceOptions
                    :labels="entry.labels"
                    :counts="entry.counts"
                    :logos="entry.logos"
                    :choice-ids="entry.choiceIds"
                    :disabled="entry.stale"
                    @choose="(choice: {label: string; choiceId: string | null}) => emit('choose', choice)" />
            </ChatBlockRow>

            <ChatBlockRow
                v-else-if="entry.kind === 'orderStatus'"
                :data-entry-id="entry.id">
                <ChatOrderStatusCard :status="entry.status" />
            </ChatBlockRow>

            <ChatBlockRow
                v-else-if="entry.kind === 'orderVerification'"
                :data-entry-id="entry.id">
                <ChatCredentialsForm
                    :disabled="entry.stale"
                    @verify="(credentials: {orderId: string; emailAddress: string}) => emit('verify-order', credentials)" />
            </ChatBlockRow>

            <ChatBlockRow
                v-else-if="entry.kind === 'returnVerification'"
                :data-entry-id="entry.id">
                <ChatCredentialsForm
                    :disabled="entry.stale"
                    @verify="(credentials: {orderId: string; emailAddress: string}) => emit('verify', credentials)" />
            </ChatBlockRow>

            <ChatBlockRow
                v-else-if="entry.kind === 'returnRequest'"
                :data-entry-id="entry.id">
                <ChatReturnRequest
                    :eligibility="entry.eligibility"
                    :disabled="entry.stale"
                    @submit="(orderLines: ReturnLine[]) => emit('submit-return', orderLines)"
                    @cancel="() => emit('cancel-return')" />
            </ChatBlockRow>

            <div
                v-else-if="entry.kind === 'typing'"
                class="chat-row">
                <ChatAvatar />
                <div class="chat-bubble chat-bubble--ai typing">
                    <span class="typing__dots">
                        <span></span>
                        <span></span>
                        <span></span>
                    </span>
                </div>
            </div>
        </template>

        <!--
            Two things the design draws that still have no data behind them. Each needs a field on
            the turn payload before it can be an entry kind here - never a guess from the message
            text, which is exactly the proxy CLAUDE.md section 4.3 rules out.

            1. Contextual suggestion pills under an answer ("Ik weet mijn kenteken niet", "Het is
               een buitenlandse auto", "Het bezorgadres klopt niet"). ChoiceOptionsTool produces
               facet choices only; free-form follow-up suggestions would be a separate list on the
               payload, written by Claude, rendered with the same .btn markup as the welcome
               topics.

            2. The hand-off card (artboard "6 · Doorverbinden"): agent availability, wait time, a
               live-chat hand-off, and the case summary sent along with it. Nothing in this chat
               talks to a human agent. The phone number and mail address do exist server-side -
               ReturnEligibility carries customerServicePhone/customerServiceMail for the
               not-eligible case, and SiteInfo/siteSettings hold both - but toCard() drops them,
               so even a contact-only version of this card needs them added to the payload first.
        -->
    </div>
</template>

<style scoped lang="scss">
    @import '../../sass/globals';
    .messages {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        gap: 16px;
        min-height: 0;
        padding: var(--chat-pad);
        overflow-y: auto;
        scrollbar-width: thin;

        border-bottom: 1px solid #e4e4e4;
    }

    /* Short conversations sit at the bottom of the panel, the way the design shows them, without
       giving up normal scrolling once the transcript outgrows the panel. */
    .messages > :first-child {
        margin-top: auto;
    }

    .messages::-webkit-scrollbar {
        width: 8px;
    }

    .messages::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: $grey;
    }

    .typing {
        display: inline-flex;
        align-items: center;
        padding: 15px 16px;
    }

    .typing__dots {
        display: inline-flex;
        gap: 4px;
    }

    .typing__dots span {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: $black;
        opacity: 0.3;
        animation: chat-typing 1s infinite ease-in-out;
    }

    .typing__dots span:nth-child(2) {
        animation-delay: 0.15s;
    }

    .typing__dots span:nth-child(3) {
        animation-delay: 0.3s;
    }

    @media (max-width: $chat-mobile) {
        .messages {
            gap: 12px;
        }
    }

    @keyframes chat-typing {
        0%,
        100% {
            opacity: 0.3;
            transform: translateY(0);
        }
        50% {
            opacity: 1;
            transform: translateY(-1px);
        }
    }
</style>
